import { useMemo, useState } from 'react';
import type { Column } from './DataTable.js';

/**
 * Search, filter, sort and paginate — once, for every table in the product.
 *
 * ── WHY A HOOK AND NOT A PROP ON EACH PAGE ────────────────────────────────
 *
 * There are eighteen tables. Written per page, "sortable" would mean eighteen
 * slightly different comparators, four of which get nulls wrong and two of which
 * sort a formatted string so "₹1,00,000" lands before "₹9,999". Filtering would
 * mean eighteen ideas of what a cleared filter looks like. The behaviour has to
 * live in one place or it is not behaviour, it is a habit.
 *
 * ── WHAT SORTS CORRECTLY, AND WHY IT IS NOT THE CELL ──────────────────────
 *
 * A column's `sort` returns the RAW comparable — minor units, an ISO date, a
 * lowercase code — never what the cell renders. `<Money>` produces
 * "₹1,72,717.67"; sorting that is sorting a string with grouping separators in
 * it, and it silently ranks a lakh below ten thousand. Same for dates rendered
 * as "8 Aug 2026". The cell is for reading; `sort` is for ordering, and they are
 * different functions of the same row.
 *
 * ── CLIENT SIDE, DELIBERATELY, AND WHERE THAT STOPS BEING TRUE ────────────
 *
 * Every table here receives its full result set already: the journal caps at 500
 * entries, the chart of accounts is 103 rows, movements 500. Sorting 500 rows in
 * the browser is instant and needs no round trip, so a filter feels like a
 * filter rather than a page load.
 *
 * That is a statement about TODAY'S volumes, not a design principle. A tenant
 * with 200,000 journal lines needs server-side paging, and the honest signal is
 * already on screen: the platform caps its reads, and `DataTable` says so when a
 * result set is truncated. When that cap starts being hit, the fix is a paged
 * endpoint, not a bigger number here.
 */

export type SortDirection = 'asc' | 'desc';

export interface SortState {
  columnId: string;
  direction: SortDirection;
}

/**
 * A filter the user can apply, built from one facet of a row.
 *
 * Options are derived from the rows themselves rather than declared, so a filter
 * can never offer a value that matches nothing — an empty result from a filter
 * you were offered reads as a broken screen.
 */
export interface Facet<Row> {
  id: string;
  label: string;
  /** The row's value for this facet. `null` leaves the row out of the facet. */
  value: (row: Row) => string | null | undefined;
  /** How the value reads in the menu. Defaults to the value itself. */
  format?: (value: string) => string;
  /**
   * Fixed option order. Use for anything ORDINAL — severity, aging, status —
   * where sorting by frequency would scramble a sequence the reader expects.
   * Without it, options are ordered by how many rows carry them.
   */
  order?: readonly string[];
}

export interface FacetOption {
  value: string;
  label: string;
  count: number;
  selected: boolean;
}

export interface ResolvedFacet {
  id: string;
  label: string;
  options: FacetOption[];
  selectedCount: number;
  toggle: (value: string) => void;
  /**
   * REPLACES the selection with exactly these values.
   *
   * This is what a drill-down needs. `toggle` is wrong for it: a summary tile
   * clicked twice would turn its own filter off, and a tile clicked after a
   * different one would leave both on and show a union nobody asked for. A
   * drill-down is "show me these rows", not "add these rows".
   */
  select: (values: string[]) => void;
  clear: () => void;
}

export interface TableView<Row> {
  /** The page's worth of rows, after search, filter and sort. */
  rows: Row[];
  /** Columns with sort handlers attached. Pass these to DataTable. */
  columns: Array<Column<Row>>;
  facets: ResolvedFacet[];
  search: string;
  onSearch: (next: string) => void;
  searchPlaceholder: string | undefined;
  sort: SortState | null;
  /** Rows matching search + filters, before paging. */
  matchCount: number;
  /** Rows handed to the hook. */
  totalCount: number;
  activeFilterCount: number;
  clearAll: () => void;
  pagination: {
    page: number;
    pageCount: number;
    onPage: (page: number) => void;
    totalItems: number;
    itemNoun?: string;
  } | undefined;
}

export interface TableViewOptions<Row> {
  columns: Array<Column<Row>>;
  facets?: Array<Facet<Row>>;
  /** Rows per page. Omit for no paging — right for a table that cannot grow. */
  pageSize?: number;
  searchPlaceholder?: string;
  initialSort?: SortState;
  /** What one row is called, for "12 of 103 settlements". */
  itemNoun?: string;
  /**
   * Filters already applied on first render, keyed by facet id.
   *
   * This is what makes a drill-down from ANOTHER page possible: the Overview
   * links to `/reconciliation?finding=return_not_reimbursed`, the page reads
   * that off the URL, and the table arrives already narrowed. Passing it as
   * initial state rather than controlled state matters — the user must be able
   * to clear or widen it once they are here, and a controlled filter would snap
   * back to the link's value every render.
   */
  initialFilters?: Record<string, string[]>;
}

/** Nulls sort last in both directions: "unknown" is not "smallest". */
function compare(a: string | number | null | undefined, b: string | number | null | undefined): number {
  const aEmpty = a === null || a === undefined || a === '';
  const bEmpty = b === null || b === undefined || b === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'en-IN', { numeric: true, sensitivity: 'base' });
}

export function useTableView<Row>(
  allRows: Row[],
  options: TableViewOptions<Row>,
): TableView<Row> {
  const {
    columns, facets = [], pageSize, searchPlaceholder, initialSort, itemNoun, initialFilters,
  } = options;

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Record<string, string[]>>(initialFilters ?? {});
  const [sort, setSort] = useState<SortState | null>(initialSort ?? null);
  const [page, setPage] = useState(0);

  // Any change to what is being shown returns to the first page. Staying on
  // page 7 of a result set that now has two pages shows an empty table, which
  // reads as "no results" rather than "you are past the end".
  const resetPage = () => setPage(0);

  const searchable = useMemo(() => columns.filter((c) => c.search), [columns]);

  const searched = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle || searchable.length === 0) return allRows;
    // Every term must match somewhere in the row, in any column. "amazon june"
    // finds the June Amazon settlement without the user knowing which column
    // holds which word.
    const terms = needle.split(/\s+/);
    return allRows.filter((row) => {
      const haystack = searchable
        .map((c) => c.search!(row) ?? '')
        .join(' ')
        .toLowerCase();
      return terms.every((t) => haystack.includes(t));
    });
  }, [allRows, search, searchable]);

  /**
   * Facet options count against the OTHER facets' filters, not this one's.
   *
   * So opening "Marketplace" while "Reconciled: no" is on shows how many
   * unreconciled rows each marketplace has — which is the question being asked.
   * Counting against this facet's own selection instead would show every
   * unselected option as 0, and the menu would look broken.
   */
  const resolvedFacets = useMemo<ResolvedFacet[]>(
    () =>
      facets.map((facet) => {
        const others = facets.filter((f) => f.id !== facet.id);
        const pool = searched.filter((row) =>
          others.every((f) => {
            const chosen = selected[f.id];
            if (!chosen?.length) return true;
            const value = f.value(row);
            return value != null && chosen.includes(value);
          }),
        );

        const counts = new Map<string, number>();
        for (const row of pool) {
          const value = facet.value(row);
          if (value == null || value === '') continue;
          counts.set(value, (counts.get(value) ?? 0) + 1);
        }

        const chosen = selected[facet.id] ?? [];
        const values = facet.order
          ? facet.order.filter((v) => counts.has(v))
          : [...counts.keys()].sort((a, b) => (counts.get(b)! - counts.get(a)!) || compare(a, b));

        return {
          id: facet.id,
          label: facet.label,
          selectedCount: chosen.length,
          options: values.map((value) => ({
            value,
            label: facet.format ? facet.format(value) : value,
            count: counts.get(value) ?? 0,
            selected: chosen.includes(value),
          })),
          toggle: (value: string) => {
            resetPage();
            setSelected((current) => {
              const list = current[facet.id] ?? [];
              const next = list.includes(value)
                ? list.filter((v) => v !== value)
                : [...list, value];
              return { ...current, [facet.id]: next };
            });
          },
          select: (values: string[]) => {
            resetPage();
            setSelected((current) => ({ ...current, [facet.id]: values }));
          },
          clear: () => {
            resetPage();
            setSelected((current) => ({ ...current, [facet.id]: [] }));
          },
        };
      }),
    // `facets` is rebuilt every render by the caller, so it cannot be a
    // dependency without looping. The values that matter are the rows and the
    // selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searched, selected],
  );

  const filtered = useMemo(
    () =>
      searched.filter((row) =>
        facets.every((facet) => {
          const chosen = selected[facet.id];
          if (!chosen?.length) return true;
          const value = facet.value(row);
          return value != null && chosen.includes(value);
        }),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searched, selected],
  );

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const column = columns.find((c) => c.id === sort.columnId);
    if (!column?.sort) return filtered;
    const direction = sort.direction === 'asc' ? 1 : -1;
    // Copied before sorting: the array belongs to the caller, and sorting a
    // prop in place is how a list mutates under a component that did not ask.
    return [...filtered].sort((a, b) => compare(column.sort!(a), column.sort!(b)) * direction);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, sort]);

  const pageCount = pageSize ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1;
  // Clamped rather than reset: a filter that shrinks the result set should land
  // the reader on the last page of what is left, not silently on page one.
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = pageSize
    ? sorted.slice(safePage * pageSize, safePage * pageSize + pageSize)
    : sorted;

  const decorated = useMemo(
    () =>
      columns.map((column) =>
        column.sort
          ? {
              ...column,
              sortDirection:
                sort?.columnId === column.id ? sort.direction : (null as SortDirection | null),
              onSort: () => {
                resetPage();
                setSort((current) =>
                  current?.columnId !== column.id
                    ? // First click on a numeric column sorts DESCENDING. On a
                      // money or count column the interesting end is the big
                      // one, and ascending would open on the smallest fee.
                      { columnId: column.id, direction: column.align === 'right' ? 'desc' : 'asc' }
                    : current.direction === 'asc'
                      ? { columnId: column.id, direction: 'desc' }
                      : // Third click clears it, back to the table's own order.
                        // A sort you cannot undo is a sort you have to reload to escape.
                        null,
                );
              },
            }
          : column,
      ),
    [columns, sort],
  );

  const activeFilterCount =
    Object.values(selected).reduce((t, list) => t + list.length, 0) + (search.trim() ? 1 : 0);

  return {
    rows: pageRows,
    columns: decorated,
    facets: resolvedFacets,
    search,
    onSearch: (next) => {
      resetPage();
      setSearch(next);
    },
    searchPlaceholder,
    sort,
    matchCount: sorted.length,
    totalCount: allRows.length,
    activeFilterCount,
    clearAll: () => {
      resetPage();
      setSearch('');
      setSelected({});
    },
    pagination:
      pageSize && sorted.length > pageSize
        ? {
            page: safePage,
            pageCount,
            onPage: setPage,
            totalItems: sorted.length,
            itemNoun,
          }
        : undefined,
  };
}
